import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function QuickReferModal({ open, onClose }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ candidate_name: "", candidate_email: "", job_id: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/jobs", { params: { status: "open" } })
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setForm({ candidate_name: "", candidate_email: "", job_id: "", note: "" });
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.candidate_name.trim() || !form.candidate_email.trim() || !form.job_id) {
      toast.error("Name, email, and role are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("candidate_name", form.candidate_name.trim());
      fd.append("candidate_email", form.candidate_email.trim());
      fd.append("job_id", form.job_id);
      if (form.note) fd.append("note", form.note);
      await api.post("/referrals", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Referral submitted — nice work!");
      onClose();
      navigate("/app/my-referrals");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="md:hidden fixed inset-0 z-[60] flex items-end bg-black/60"
      onClick={onClose}
      data-testid="quick-refer-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white border-t-2 border-black rounded-t-3xl p-5 pb-[calc(env(safe-area-inset-bottom)+20px)] animate-[slideUp_200ms_ease-out]"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-white" strokeWidth={2.6} />
            </div>
            <h3 className="font-display font-black text-lg">Quick refer</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center"
            aria-label="Close"
            data-testid="quick-refer-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <input
            className="input-brutal text-base"
            placeholder="Candidate full name"
            value={form.candidate_name}
            onChange={(e) => setForm({ ...form, candidate_name: e.target.value })}
            data-testid="quick-refer-name"
            autoFocus
          />
          <input
            className="input-brutal text-base"
            type="email"
            placeholder="Candidate email"
            value={form.candidate_email}
            onChange={(e) => setForm({ ...form, candidate_email: e.target.value })}
            data-testid="quick-refer-email"
          />
          <select
            className="input-brutal text-base"
            value={form.job_id}
            onChange={(e) => setForm({ ...form, job_id: e.target.value })}
            data-testid="quick-refer-job"
          >
            <option value="">Select an open role…</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title} {j.location ? `· ${j.location}` : ""}
              </option>
            ))}
          </select>
          <textarea
            className="input-brutal text-base"
            rows={2}
            placeholder="Short note (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="quick-refer-note"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            type="button"
            onClick={() => { onClose(); navigate("/app/refer"); }}
            className="btn-brutal btn-white text-sm"
            data-testid="quick-refer-full"
          >
            Full form <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="submit"
            disabled={busy}
            className="btn-brutal btn-primary text-sm"
            data-testid="quick-refer-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {busy ? "Submitting…" : "Submit"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 text-center">
          Prefer more detail? Tap <strong>Full form</strong> for the web version.
        </p>
      </form>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
